import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { listingSheetsApi, type ListingSheet } from "@/slices/listingSheets/listingSheetsApi";

/**
 * Standalone listing API (REP-211).
 *
 * The per-rep-agreement listing sheet lives at `/api/listing-sheets` (see
 * listingSheetsApi). This slice owns the NEW standalone listing surface:
 *
 *   - POST /api/listings              → create a listing WITHOUT a rep agreement
 *   - POST /api/listings/:id/link-rep → tie an existing standalone listing to a
 *                                       seller/landlord rep project
 *
 * PINNED backend contracts:
 *   - POST /api/listings  body = listing fields (mirrors the rep-agreement
 *     listing box) incl. `propertyId` → `{ data: listing }`
 *   - POST /api/listings/:id/link-rep  body `{ projectId }` → `{ data: listing }`
 *
 * The returned `listing` is assumed to be a ListingSheet-shaped DTO; we route
 * to `/listings/:id` (the standalone listing detail page). On link, we
 * invalidate the listing-sheets cache so the detail banner re-evaluates.
 */

/** Body for POST /api/listings — mirrors the rep-agreement listing form. */
export interface CreateStandaloneListingBody {
  propertyId: string;
  // Section 1 / 9 fields mirrored from ListingSheetStepCard.
  relationshipType?: ListingSheet["relationshipType"];
  targetLaunchDate?: string;
  offering?: ListingSheet["offering"];
  askingPrice?: number;
  askingLeaseRatePerSfPerYr?: number;
  targetCapRate?: number;
  desiredTimeline?: string;
  // free-form name for the listing (optional — server may derive from property)
  internalName?: string;
}

export const listingsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "listingsApi",
    baseUrl: "/api/listings",
  }),
  reducerPath: "listingsApi",
  tagTypes: ["Listing"],
  endpoints: (builder) => ({
    /** GET /api/listings/:id — resolve a standalone listing by its OWN id
     *  (standalone listings have no rep project to key the detail page on). */
    getListingById: builder.query<{ data: ListingSheet }, string>({
      query: (id) => `/${id}`,
      providesTags: (_r, _e, id) => [{ type: "Listing", id }],
    }),

    /** POST /api/listings — standalone create (no rep agreement). */
    createStandaloneListing: builder.mutation<
      { data: ListingSheet },
      CreateStandaloneListingBody
    >({
      query: (body) => ({
        url: "/",
        method: "POST",
        body,
      }),
      // The standalone listing surfaces on the same /listings page that reads
      // listingSheetsApi — bust that cache so the new listing shows up.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(listingSheetsApi.util.invalidateTags(["ListingSheets"]));
        } catch {
          /* best-effort */
        }
      },
    }),

    /** POST /api/listings/:id/link-rep — tie listing to an existing rep project. */
    linkListingToRep: builder.mutation<
      { data: ListingSheet },
      { id: string; projectId: string }
    >({
      query: ({ id, projectId }) => ({
        url: `/${id}/link-rep`,
        method: "POST",
        body: { projectId },
      }),
      // The detail page reads the listing through getListingById ({ type:
      // "Listing", id }) — invalidate THAT so the page refetches and the
      // tie-to-rep banner disappears the moment the tie lands. Also bust the
      // cross-slice listing-sheet caches feeding the /listings list + the rep
      // deal's onboarding view.
      invalidatesTags: (_r, _e, { id }) => [{ type: "Listing", id }],
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            listingSheetsApi.util.invalidateTags([
              "ListingSheets",
              { type: "ListingSheetByProject", id: projectId },
            ]),
          );
        } catch {
          /* best-effort */
        }
      },
    }),
  }),
});

export const {
  useGetListingByIdQuery,
  useCreateStandaloneListingMutation,
  useLinkListingToRepMutation,
} = listingsApi;
